import {ReactNode} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {carAuditApi} from '@dms/api/core';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {
  Form,
  updateAuditStructureSettings,
  selectStructureSetting,
  selectVehicleFeatures,
  BasicCategoryOfStructure,
  CreateTypeOfControlSettingsRequestBody,
  useApiDispatch,
  AuditCategoryUniqueKey,
} from '@dms/teas';

import {useNavigate} from 'shared/web';

import {AuditStructureSettingsForm} from '../types';
import {getUpdatedInspectionType} from '../utils/getUpdatedInspectionType';
import {getFormSchema, getRequestBodyItemsFormValues} from '../utils/legacy_utils';
import {getEquipmentIdsFromCategories, getEquipmentUniqueKeyToIdDictionary} from '../utils/utils';

interface AuditSettingsFormProps {
  type: CreateTypeOfControlSettingsRequestBody['inspectionType'];
  isCreate?: boolean;
  structureVersion: number;
  categories: BasicCategoryOfStructure[];
  children?: ReactNode;
}

export const AuditSettingsForm = ({
  type,
  isCreate,
  structureVersion,
  children,
  categories,
}: AuditSettingsFormProps) => {
  const structureSettingData = useSelector(selectStructureSetting);
  const apiDispatch = useApiDispatch();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const features = useSelector(selectVehicleFeatures('VEHICLETYPE_PASSENGER_CAR'));

  const equipmentCategory = categories?.find(
    (cat) => cat.uniqueKey === AuditCategoryUniqueKey.EQUIPMENT
  );

  const uniqueKeyToIdDictionary = getEquipmentUniqueKeyToIdDictionary(equipmentCategory);

  const mainEquipmentIds = features.map((feature) => uniqueKeyToIdDictionary[feature.value]);

  const isVehicleCondition =
    type === CreateTypeOfControlSettingsRequestBody.inspectionType.VEHICLE_CONDITION;

  const equipmentIds = getEquipmentIdsFromCategories(categories);

  const handleSubmit = (values: AuditStructureSettingsForm) => {
    // eslint-disable-next-line no-restricted-syntax -- apiDispatch is not RTK Query, it's a thunk action
    apiDispatch(updateAuditStructureSettings.action, {
      requestBody: {
        name: values.name,
        key: isCreate ? null : (structureSettingData?.key ?? null),
        showEmptyFields: !values.showEmptyFields,
        inspectionType: getUpdatedInspectionType(type, values.validationInspection),
        validation: values.validation
          ? CreateTypeOfControlSettingsRequestBody.validation.VALIDATE
          : CreateTypeOfControlSettingsRequestBody.validation.NON_VALIDATE,
        items: getRequestBodyItemsFormValues(
          values,
          uniqueKeyToIdDictionary,
          features,
          equipmentIds
        ),
        version: structureVersion,
      },
    })
      .then(() => {
        dispatch(carAuditApi.util.invalidateTags(['InspectionTemplates']));

        navigate(
          isVehicleCondition ? settingsRoutes.vehicleCondition : settingsRoutes.vehicleInspections
        );
      })
      .catch(handleApiError);
  };

  const getInitialValues = (): AuditStructureSettingsForm => {
    const initialValues: AuditStructureSettingsForm = {
      categories: {},
      paramDefinitions: {},
      validationInspection: type === 'validationInspection',
      name: '',
      showEmptyFields: !structureSettingData?.showEmptyFields,
      validation: false,
      equipmentValues: [],
    };

    const isEquipmentVisible = structureSettingData?.items.find(
      (data) => data.categoryId === equipmentCategory?.id
    )?.visible;

    if (isCreate) {
      return initialValues;
    }

    structureSettingData?.items?.forEach((item) => {
      const {categoryId, paramDefinitionId, visible, mandatory} = item;

      if (categoryId) {
        initialValues.categories[categoryId] = visible;
      }

      if (paramDefinitionId) {
        if (equipmentIds.includes(paramDefinitionId)) {
          if (visible && mainEquipmentIds.includes(paramDefinitionId)) {
            initialValues.equipmentValues?.push(paramDefinitionId);
          }
        } else {
          initialValues.paramDefinitions[paramDefinitionId] = {
            visible,
            mandatory,
          };
        }
      }
    });

    if (isNilOrEmpty(initialValues.equipmentValues) && isEquipmentVisible) {
      initialValues.equipmentValues = mainEquipmentIds;
    }
    const moreEquipment = equipmentCategory?.childCategories?.[0]?.childCategories?.find(
      (cat) => cat.uniqueKey === AuditCategoryUniqueKey.MORE_EQUIPMENT
    );

    // needed so user could add more equipment in inspection
    moreEquipment && initialValues.equipmentValues?.push(moreEquipment.id);

    return {
      ...initialValues,
      name: structureSettingData?.name ?? '',
      validation:
        structureSettingData?.validation ===
        CreateTypeOfControlSettingsRequestBody.validation.VALIDATE,
    };
  };

  return (
    <Form<AuditStructureSettingsForm>
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      isFormSaverDisabled
      schema={isVehicleCondition ? undefined : getFormSchema()}
      mutators={{
        setFieldValue: ([field, value], state, {changeValue}) => {
          changeValue(state, field, () => value);
        },
      }}
      render={() => <>{children}</>}
    />
  );
};
